/**
 * WebBridge
 * Web 瀏覽器環境的 Bridge 實作（使用 Mock 資料）
 */

import { MOCK_STUDENTS } from '@/config/mockData'
import type {
  Student,
  GameInfo,
  StudentPickedEvent,
  StudentRemovedEvent,
} from '../types/bridge.types'
import { BridgeAction } from '../types/bridge.types'
import type { PlatformType } from '../types/platform.types'
import { matchStudent, studentKey } from '@/utils'
import { BridgeStrategy } from './BridgeStrategy'

export class WebBridge extends BridgeStrategy {
  private mockStudents: Student[] = [...MOCK_STUDENTS]
  // Set keys are produced by `studentKey(student)` so they cope with both
  // null studentId (new host) and null serialNumber (legacy host) cases.
  private removedStudents: Set<string> = new Set()
  private selectedStudents: Set<string> = new Set()

  getPlatform(): PlatformType {
    return 'web'
  }

  async connect(): Promise<void> {
    try {
      this.updateConnectionStatus('connecting')

      // 模擬連接延遲
      await this.simulateDelay(500)

      this.setupListeners()
      this.updateConnectionStatus('connected')
      this.updateLastActivity()

      console.log('Web bridge (Mock) connected successfully')
      console.log('Mock mode: Using simulated data for development')
    } catch (error) {
      this.updateConnectionStatus('error')
      console.error('Failed to connect Web bridge:', error)
      throw error
    }
  }

  disconnect(): void {
    this.updateConnectionStatus('disconnected')
    this.cleanup()

    // 重置 mock 資料
    this.mockStudents = [...MOCK_STUDENTS]
    this.removedStudents.clear()
    this.selectedStudents.clear()

    console.log('Web bridge (Mock) disconnected')
  }

  setupListeners(): void {
    // Web Mock 不需要設置實際的監聽器
    console.log('Web bridge mock listeners initialized')
  }

  /**
   * 發送訊息（Mock 實作）
   */
  async sendMessage<T>(action: BridgeAction, data?: unknown): Promise<T> {
    console.log(`[WebBridge Mock] ${action}:`, data)

    // 模擬延遲
    await this.simulateDelay(300)

    this.updateLastActivity()

    // 根據 action 返回 Mock 資料
    switch (action) {
      case BridgeAction.GET_STUDENT_LIST:
        return this.mockGetStudentList() as T

      case BridgeAction.STUDENT_PICKED:
        return this.mockStudentPicked(data) as T

      case BridgeAction.STUDENT_REMOVED:
        return this.mockStudentRemoved(data) as T

      case BridgeAction.GET_ACCESS_TOKEN:
        return this.mockGetAccessToken() as T

      case BridgeAction.GET_CURRENT_GAME_INFO:
        return this.mockGetCurrentGameInfo() as T

      case BridgeAction.SEND_END_GAME:
        return this.mockSendEndGame(data) as T

      default:
        console.warn(`[WebBridge] Unknown action: ${action}`)
        return null as T
    }
  }

  /**
   * Mock: 獲取學生列表
   *
   * 回傳完整班級名單。`STUDENT_REMOVED` 事件是 spinner session-local 的概念
   * （老師在抽中後從本輪抽籤池移除），不影響 class roster；refresh 應能讓
   * 先前被移除的學生重新出現於轉盤。同時 reset 本機 session 狀態，模擬
   * 「重新拉名單」的乾淨起點。
   */
  private mockGetStudentList(): Student[] {
    this.removedStudents.clear()
    this.selectedStudents.clear()
    return this.mockStudents
  }

  private parseStudentEvent(
    data: unknown
  ): StudentPickedEvent | StudentRemovedEvent | null {
    if (!data || typeof data !== 'object') return null
    const obj = data as Record<string, unknown>
    if (!('studentId' in obj) && !('serialNumber' in obj)) return null

    const studentId =
      typeof obj.studentId === 'string' ? obj.studentId : null
    const serialNumber =
      typeof obj.serialNumber === 'number' ? obj.serialNumber : null

    if (studentId === null && serialNumber === null) return null
    return { studentId, serialNumber }
  }

  /**
   * Mock: 學生被選中
   */
  private mockStudentPicked(data: unknown): boolean {
    const event = this.parseStudentEvent(data)
    if (!event) return false

    const student = this.mockStudents.find(s => matchStudent(event, s))

    if (!student) {
      console.warn('Student not found for pick event', event)
      return false
    }

    const key = studentKey(student)
    if (this.removedStudents.has(key)) {
      console.warn(`Student ${student.name} has been removed`)
      return false
    }

    this.selectedStudents.add(key)
    console.log(`Student picked: ${student.name} (${key})`)
    return true
  }

  /**
   * Mock: 學生被移除
   */
  private mockStudentRemoved(data: unknown): boolean {
    const event = this.parseStudentEvent(data)
    if (!event) return false

    const student = this.mockStudents.find(s => matchStudent(event, s))

    if (!student) {
      console.warn('Student not found for remove event', event)
      return false
    }

    const key = studentKey(student)
    if (this.removedStudents.has(key)) {
      console.warn(`Student ${student.name} already removed`)
      return false
    }

    // 檢查是否剩餘最後兩名學生
    const activeCount = this.mockStudents.filter(
      s => !this.removedStudents.has(studentKey(s))
    ).length

    if (activeCount <= 2) {
      console.warn('Cannot remove student: minimum 2 students required')

      setTimeout(() => {
        this.eventEmitter.emit('error', {
          message: '無法移除學生：至少需要保留 2 名學生',
          code: 'MIN_STUDENTS_REQUIRED',
        })
      }, 0)

      return false
    }

    this.removedStudents.add(key)
    this.selectedStudents.delete(key)

    console.log(`Student removed: ${student.name} (${key})`)
    return true
  }

  /**
   * Mock: 獲取 Access Token
   */
  private mockGetAccessToken(): string {
    const token = 'mock-token-' + Date.now()
    console.log(`Generated mock access token: ${token}`)
    return token
  }

  /**
   * Mock: 獲取當前遊戲資訊
   */
  private mockGetCurrentGameInfo(): GameInfo {
    const gameInfo: GameInfo = {
      gameId: '',
      title: 'Mock 遊戲排行榜',
      lessonId: 'mock-lesson-123',
      isEndGame: false,
    }
    console.log(`Returning mock game info:`, gameInfo)
    return gameInfo
  }

  /**
   * Mock: 發送結束遊戲通知
   */
  private mockSendEndGame(data: unknown): void {
    console.log(`Mock end game event:`, data)
    setTimeout(() => {
      this.eventEmitter.emit('gameEndSent', data)
    }, 0)
  }

  /**
   * 模擬網路延遲
   */
  private simulateDelay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * 獲取 Mock 資料狀態（用於開發除錯）
   */
  getMockStatus(): {
    totalStudents: number
    activeStudents: number
    removedStudents: number
    selectedStudents: number
  } {
    const activeCount = this.mockStudents.filter(
      s => !this.removedStudents.has(studentKey(s))
    ).length

    return {
      totalStudents: this.mockStudents.length,
      activeStudents: activeCount,
      removedStudents: this.removedStudents.size,
      selectedStudents: this.selectedStudents.size,
    }
  }

  /**
   * 重置 Mock 資料（用於開發測試）
   */
  resetMockData(): void {
    this.mockStudents = [...MOCK_STUDENTS]
    this.removedStudents.clear()
    this.selectedStudents.clear()

    console.log('Mock data reset to initial state')

    this.mockGetStudentList()
  }

  /**
   * 添加自訂學生（用於測試）
   */
  addMockStudent(student: Student): void {
    if (this.mockStudents.find(s => matchStudent(student, s))) {
      console.warn(`Student ${student.name} already exists`)
      return
    }

    this.mockStudents.push(student)
    console.log(`Added mock student: ${student.name}`)

    this.mockGetStudentList()
  }
}
